import React, { useState, useRef } from "react";
import {
  Button,
  Box,
  Flex,
  Table,
  LoadingOverlay,
  TextInput,
  SimpleGrid,
  ActionIcon,
  UnstyledButton,
  ThemeIcon,
  Stack,
  Text,
  Group,
} from "@mantine/core";
import axios from "axios";
import {
  getCoreRowModel,
  useReactTable,
  getFilteredRowModel,
  flexRender,
  getPaginationRowModel,
} from "@tanstack/react-table";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { IconDeviceFloppy } from "@tabler/icons-react";
import entityIcon from "@util/entityIcon";

export default function SavedReportRecipeList({
  recipeId,
  onSelect,
  selectedId,
}) {
  const [searchValue, setSearchValue] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageCount, setPageCount] = useState(-1);
  const queryKey = `savedRecipeList`;
  const queryClient = useQueryClient();

  const columns = [
    {
      header: () => "",
      accessorKey: "id",
      id: "actions",
      width: 10,
      cell: (info) => (
        <Button
          size="xs"
          color="green"
          radius="xl"
          disabled={selectedId === info.row.original.id}
          onClick={() => {
            onSelect(info.row.original);
          }}
        >
          View
        </Button>
      ),
    },
    {
      header: () => "Title",
      width: 250,
      accessorKey: "title",
      cell: (info) => info.getValue(),
    },
    {
      header: () => "Description",
      // width: 600,
      accessorKey: "description",
      cell: (info) => info.getValue(),
    },
  ];

  const table = useReactTable({
    manualPagination: true,
    pageCount,
    columns,
    data: results,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 10,
      },
    },
  });

  const CancelToken = axios.CancelToken;
  const cancelRef = useRef(null);

  const {
    failureReason: error,
    data: info,
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: [queryKey, recipeId, searchValue],
    // refetchOnMount: false,
    queryFn: async () => {
      return axios
        .post(
          `/report-recipes/${recipeId}/saved-recipes/`,
          {
            page_size: table.getState().pagination.pageSize,
            page:
              table.getState().pagination.pageIndex === -1
                ? 0
                : table.getState().pagination.pageIndex,
            search: searchValue,
          },
          {
            cancelToken: new CancelToken(function executor(c) {
              cancelRef.current = c;
            }),
          }
        )
        .then(({ data }) => {
          setResults(
            data.response[0].data.map((m) => ({
              ...m,
            }))
          );
          setPageCount(data.response[0].page_count);
          setTotal(data.response[0].total);
          return data.response[0];
        })
        .catch((err) => {
          console.log(err);
        });
    },
  });

  return (
    <div style={{ position: "relative" }}>
      <LoadingOverlay
        visible={isLoading || isFetching}
        loaderProps={{ color: "dark" }}
        zIndex={99}
      />
      <TextInput
        value={searchValue}
        onChange={(e) => {
          setSearchValue(e.target.value);
        }}
        placeholder="Search saved recipes"
        mb="sm"
      />
      <Box
        style={{
          maxHeight: "500px",
          minHeight: "500px",
          height: "500px",
          overflowY: "auto",
        }}
      >
        {results.length === 0 && (
          <Text size="sm" c="dimmed">
            No saved recipes yet
          </Text>
        )}
        <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
          {table.getRowModel().rows.map((result) => (
            <RecipeCard
              key={result.id}
              recipe={result.original}
              isSelected={selectedId === result.original.id}
              onSelect={onSelect}
            />
          ))}
        </SimpleGrid>
        {/* <Table striped>
          <Table.Tbody>
            {results.length === 0 && (
              <Table.Tr>
                <Table.Td>No saved recipes yet</Table.Td>
              </Table.Tr>
            )}
            {table.getRowModel().rows.map((row) => (
              <Table.Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Table.Td
                    key={cell.id}
                    style={{
                      width: `${cell.column.columnDef.width}px`,
                    }}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table> */}
      </Box>
      {pageCount > 1 && (
        <Flex align="center" mt="lg" gap="xs">
          <Button
            size="xs"
            variant="light"
            fullWidth
            disabled={table.getState().pagination.pageIndex === 0}
            onClick={() =>
              table.setPageIndex(table.getState().pagination.pageIndex - 1)
            }
          >
            Back
          </Button>
          <Button
            size="xs"
            fullWidth
            variant="light"
            disabled={table.getState().pagination.pageIndex === pageCount - 1}
            onClick={() =>
              table.setPageIndex(table.getState().pagination.pageIndex + 1)
            }
          >
            Next
          </Button>
        </Flex>
      )}
    </div>
  );
}

const RecipeCard = ({ recipe, isSelected, onSelect }) => {
  return (
    <UnstyledButton
      onClick={() => onSelect(isSelected ? null : recipe)}
      style={{
        width: "100%",
      }}
    >
      <Box
        key={recipe.id}
        withBorder
        padding="xs"
        radius="md"
        style={{
          border: `1px solid ${
            isSelected
              ? "var(--mantine-color-teal-3)"
              : "var(--mantine-color-gray-3)"
          }`,
          borderRadius: "var(--mantine-radius-md)",
          backgroundColor: isSelected ? "var(--mantine-color-teal-0)" : "#fff",
          transition: "all 0.2s ease",
        }}
      >
        <Group gap="sm" wrap="nowrap" style={{ alignItems: "stretch" }}>
          <Box
            style={{
              background: isSelected
                ? "var(--mantine-color-teal-6)"
                : "var(--mantine-color-gray-2)",
              borderRadius:
                "var(--mantine-radius-md) 0 0 var(--mantine-radius-md)",
              // height: "95px",
              flexShrink: 0,
              flexGrow: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "var(--mantine-spacing-xs)",
              width: "60px",
              alignSelf: "stretch",
            }}
          >
            <ThemeIcon
              radius="xl"
              variant={isSelected ? "filled" : "light"}
              size="xl"
              color={isSelected ? "teal" : "teal"}
            >
              <IconDeviceFloppy size={24} />
            </ThemeIcon>
          </Box>
          <Stack
            gap={1}
            style={{ flexGrow: 1, padding: "var(--mantine-spacing-xs)" }}
          >
            <Text fw={600} size="md" c="dark.5" lineClamp={1}>
              {recipe.title || "Untitled"}
            </Text>
            <Text size="sm" style={{ maxWidth: "90%" }} lineClamp={1}>
              {recipe.description || "No description provided"}
            </Text>
            {recipe.created_at && (
              <Text c="dimmed" size="xs">
                saved {new Date(recipe.created_at).toLocaleString()}
              </Text>
            )}
          </Stack>
        </Group>
      </Box>
    </UnstyledButton>
  );
};
